/**
 * In-memory LRU of parsed, canonicalized version states (migration 006 UI).
 *
 * Cloud versions are IMMUTABLE — the save flow only ever appends
 * project_versions rows — so entries never need invalidation; the cap exists
 * purely to bound memory (a state is a few hundred KB; 8 ≈ one modal session
 * of adjacent-row diffs). Values are the FROZEN objects readStateFromZip
 * returns: one object is handed to every caller, and freeze is what makes
 * that sharing safe.
 *
 * The factory is the test seam — tests inject counting fakes for the fetch and
 * the read. The default instance lazy-imports its dependencies so this module
 * stays out of the pure export-test graph (auth-api pulls the whole template
 * client; save-load pulls the store chain).
 */

export function createVersionStateCache({ fetchBundle, read, cap = 8 } = {}) {
  if (typeof fetchBundle !== 'function' || typeof read !== 'function') {
    throw new Error('createVersionStateCache requires fetchBundle and read functions');
  }
  // key `${projectId}:${version}` → Promise<frozen state>. The PROMISE is
  // cached so concurrent gets share one in-flight fetch; a rejection deletes
  // the entry, making failures retryable instead of poisoning the key.
  const entries = new Map();

  function touch(key, value) {
    entries.delete(key);
    entries.set(key, value);
    while (entries.size > cap) {
      entries.delete(entries.keys().next().value);
    }
  }

  return {
    get(projectId, version) {
      const key = `${projectId}:${version}`;
      const hit = entries.get(key);
      if (hit) {
        touch(key, hit);
        return hit;
      }
      const promise = (async () => {
        const blob = await fetchBundle(projectId, version);
        return await read(blob);
      })();
      promise.catch(() => {
        if (entries.get(key) === promise) entries.delete(key);
      });
      touch(key, promise);
      return promise;
    },
    get size() {
      return entries.size;
    },
    _clearForTesting() {
      entries.clear();
    }
  };
}

export const versionStateCache = createVersionStateCache({
  fetchBundle: (projectId, version) =>
    import('../templates/auth-api.js').then((mod) =>
      mod.getProjectVersionBundle(projectId, version)
    ),
  read: (blob) => import('./save-load.js').then((mod) => mod.readStateFromZip(blob))
});

/** Convenience wrapper the modal uses. */
export function getVersionState(projectId, version) {
  return versionStateCache.get(projectId, version);
}
